/**
 * Firestore write functions and callable wrapper that refuse to run while a
 * sys_admin is viewing the app as another member (see utils/viewAs.js).
 *
 * Import writes from here instead of 'firebase/firestore' / 'firebase/functions'.
 * The Firestore rules and Cloud Functions enforce the same thing server-side;
 * this layer exists so the operator gets a clear message instead of a
 * permissions error, and so nothing is attempted in the first place.
 */
import {
  setDoc as fsSetDoc,
  updateDoc as fsUpdateDoc,
  addDoc as fsAddDoc,
  deleteDoc as fsDeleteDoc,
  runTransaction as fsRunTransaction,
  writeBatch as fsWriteBatch,
} from 'firebase/firestore';
import { httpsCallable as fsHttpsCallable } from 'firebase/functions';
import { assertCanWrite } from '../utils/viewAs';

export const setDoc = (...args) => { assertCanWrite(); return fsSetDoc(...args); };
export const updateDoc = (...args) => { assertCanWrite(); return fsUpdateDoc(...args); };
export const addDoc = (...args) => { assertCanWrite(); return fsAddDoc(...args); };
export const deleteDoc = (...args) => { assertCanWrite(); return fsDeleteDoc(...args); };
export const runTransaction = (...args) => { assertCanWrite(); return fsRunTransaction(...args); };

export const writeBatch = (...args) => {
  const batch = fsWriteBatch(...args);
  const commit = batch.commit.bind(batch);
  batch.commit = () => { assertCanWrite(); return commit(); };
  return batch;
};

// Callables that only read. Must match the `readOnly` declarations in
// functions/index.js; anything not listed here is treated as a write.
const READ_ONLY_CALLABLES = {
  analyzeDataCompleteness: true,
  getEmailReportLogs: true,
  searchM365Users: true,
  compareGenderData: true,
  getCCBV2AuthStatus: true,
  manageReportSchedules: (data) => data?.action === 'list',
};

export const httpsCallable = (functions, name, options) => {
  const callable = fsHttpsCallable(functions, name, options);
  return (data) => {
    const readOnly = READ_ONLY_CALLABLES[name];
    const allowed = typeof readOnly === 'function' ? readOnly(data) : readOnly === true;
    if (!allowed) assertCanWrite();
    return callable(data);
  };
};
